// src/pages/readingPrefs.js
//
// Pure, framework-free reading-preferences logic for the shared reader shell
// (ReaderPageShell). Kept side-effect-free so normalization + class derivation are
// unit-testable without React/DOM. The hook (useReadingPrefs.js) wraps localStorage
// around this.
//
// Vocabulary: colorMode 'night'|'day'(default)|'muji'; readingScale
// 'normal'(default)|'medium'|'large'. The classes are applied to <html> by App.jsx and
// read by the token sets in src/index.css.
// 'muji' = the site-wide "Minimalist" skin (user-facing label "Minimalist"); the
// internal value stays 'muji' to avoid churn.
// DEFAULT is 'day' since Phase 1a: Direction A is light-first. Anyone with a stored
// preference keeps it -- normalizePrefs only falls back for missing/garbage values.

export const COLOR_MODES = ['night', 'day', 'muji'];
export const READING_SCALES = ['normal', 'medium', 'large'];
export const DEFAULT_PREFS = { colorMode: 'day', readingScale: 'normal' };
export const READING_PREFS_KEY = 'btl.readingPrefs';

// Clamp any stored/garbage value to a valid prefs object (defaults for anything off).
export function normalizePrefs(raw) {
    const r = raw && typeof raw === 'object' ? raw : {};
    return {
        colorMode: COLOR_MODES.includes(r.colorMode) ? r.colorMode : DEFAULT_PREFS.colorMode,
        readingScale: READING_SCALES.includes(r.readingScale) ? r.readingScale : DEFAULT_PREFS.readingScale,
    };
}

// The class string applied to the reader shell root.
//   day  -> 'reading-light pack-themed' (the light skin needs BOTH classes, per index.css)
//   muji -> 'reading-muji pack-themed'  (the Minimalist skin, same both-classes pattern)
//   night-> neither (root stays identical to the existing dark reader)
//   scale-> 'reading-scale-medium' | 'reading-scale-large' | '' (normal -> --font-scale 1)
export function readerShellClasses({ colorMode, readingScale } = {}) {
    const parts = [];
    if (colorMode === 'day') parts.push('reading-light', 'pack-themed');
    else if (colorMode === 'muji') parts.push('reading-muji', 'pack-themed');
    if (readingScale === 'medium') parts.push('reading-scale-medium');
    else if (readingScale === 'large') parts.push('reading-scale-large');
    return parts.join(' ');
}

// Skin class for PORTALED reader surfaces (gloss popover, model-essay modal, AI +
// Notes drawers). Portals keep React context but NOT CSS ancestor classes, so each
// re-applies the skin from the live colorMode. Mirrors readerShellClasses' color part
// but always includes pack-themed (night emits 'pack-themed' alone -- inert, since no
// bare .pack-themed CSS exists -- preserving the existing night-mode portal output).
export function portalSkinClass(colorMode) {
    if (colorMode === 'day') return 'reading-light pack-themed';
    if (colorMode === 'muji') return 'reading-muji pack-themed';
    return 'pack-themed';
}
